/**
 * Inicialización de Sentry — GUIA_REACT_v2 §7.2.
 *
 * Se activa solo si `VITE_SENTRY_DSN` está definido. En dev sin DSN no se
 * configura nada (cero overhead).
 *
 * Privacidad: `beforeSend` y `beforeBreadcrumb` filtran emails, teléfonos y
 * cuerpos de request para evitar que datos de leads lleguen a Sentry.
 */
import * as Sentry from '@sentry/react';

// Patrones para detectar PII en strings (case-insensitive).
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /(\+?\d[\d\s\-().]{7,}\d)/g;

/** Reemplaza emails y teléfonos en cualquier string por placeholders. */
function scrubString(input: string): string {
  return input.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[phone]');
}

/** Recorre un objeto y limpia strings; no muta el original. */
function scrubDeep<T>(value: T, depth = 0): T {
  if (depth > 6) return value; // límite de profundidad por seguridad
  if (typeof value === 'string') return scrubString(value) as unknown as T;
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Campos sensibles del lead: nunca enviar el valor real.
      if (['email', 'telefono', 'nombre', 'mensaje', 'password', 'token'].includes(k)) {
        out[k] = '[redacted]';
      } else {
        out[k] = scrubDeep(v, depth + 1);
      }
    }
    return out as unknown as T;
  }
  return value;
}

export function initMonitoring(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || 'dev',
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.0, // habilitar cuando se necesite Session Replay
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      // Scrub data del request (form submissions a /leads no deben filtrar PII).
      if (event.request?.data) {
        event.request.data = scrubDeep(event.request.data);
      }
      if (event.request?.cookies) delete event.request.cookies;
      if (event.user) delete event.user;
      if (event.contexts) {
        event.contexts = scrubDeep(event.contexts) as typeof event.contexts;
      }
      if (event.extra) {
        event.extra = scrubDeep(event.extra) as typeof event.extra;
      }
      // Mensajes y exceptions también pueden traer interpolaciones con PII.
      if (event.message) event.message = scrubString(event.message);
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex.value) ex.value = scrubString(ex.value);
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Breadcrumbs de fetch/xhr suelen incluir URLs y bodies — limpiamos.
      if (breadcrumb.data) {
        breadcrumb.data = scrubDeep(breadcrumb.data);
      }
      if (breadcrumb.message) {
        breadcrumb.message = scrubString(breadcrumb.message);
      }
      return breadcrumb;
    },
  });
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('extra', context);
      Sentry.captureException(err);
    });
  } else {
    Sentry.captureException(err);
  }
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
